/**
 * Personelin tüm izin taleplerini listeler.
 * Bekleyen veya onaylı talepler iptal edilebilir.
 */

import { useCallback, useEffect, useState } from 'react'
import { Calendar, X, Check, Clock, XCircle, MessageSquare, Trash2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAuth } from '@/contexts/AuthContext'
import {
  izinleriGetir, izinIptal,
  DURUM_ETIKETLERI, KAYNAK_ETIKETLERI,
  type IzinTalebi,
} from '@/lib/services/izin-service'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel
  onKapat: () => void
  onDegisiklik?: () => void
}

function tarihEtiketi(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function durumRengi(d: IzinTalebi['durum']): string {
  switch (d) {
    case 'onaylandi': return 'bg-green-100 text-green-800 border-green-200'
    case 'reddedildi': return 'bg-red-100 text-red-800 border-red-200'
    case 'iptal': return 'bg-gray-100 text-gray-700 border-gray-200'
    default: return 'bg-yellow-100 text-yellow-800 border-yellow-200'
  }
}

function DurumIkonu({ durum }: { durum: IzinTalebi['durum'] }) {
  switch (durum) {
    case 'onaylandi': return <Check className="w-3.5 h-3.5" />
    case 'reddedildi': return <XCircle className="w-3.5 h-3.5" />
    case 'iptal': return <X className="w-3.5 h-3.5" />
    default: return <Clock className="w-3.5 h-3.5" />
  }
}

export function IzinlerimModal({ acik, personel, onKapat, onDegisiklik }: Props) {
  const { firebaseUser } = useAuth()
  const [izinler, setIzinler] = useState<IzinTalebi[]>([])
  const [yukleniyor, setYukleniyor] = useState(false)
  const [iptalEdilecek, setIptalEdilecek] = useState<IzinTalebi | null>(null)

  const yukle = useCallback(async () => {
    setYukleniyor(true)
    try {
      const list = await izinleriGetir({ personelId: personel.id })
      setIzinler(list)
    } catch (err) {
      console.error('İzinler yüklenemedi:', err)
    } finally {
      setYukleniyor(false)
    }
  }, [personel.id])

  useEffect(() => {
    if (acik) yukle()
  }, [acik, yukle])

  async function iptalEt() {
    if (!iptalEdilecek || !firebaseUser) return
    try {
      await izinIptal(iptalEdilecek.id, firebaseUser.uid, 'Personel tarafından iptal edildi')
      setIptalEdilecek(null)
      await yukle()
      onDegisiklik?.()
    } catch (err) {
      alert('İptal edilemedi: ' + (err as Error).message)
    }
  }

  return (
    <>
      <Modal acik={acik} onKapat={onKapat} baslik="İzin Geçmişim" boyut="lg">
        {yukleniyor ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          </div>
        ) : izinler.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-700">Henüz izin talebin yok</p>
          </div>
        ) : (
          <div className="space-y-2">
            {izinler.map((i) => {
              const iptalEdilebilir = i.durum === 'bekliyor' || i.durum === 'onaylandi'
              return (
                <div key={i.id} className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900">
                        {tarihEtiketi(i.baslangicTarihi)}
                        {i.gunSayisi > 1 && (
                          <span className="text-gray-500"> – {tarihEtiketi(i.bitisTarihi)}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {i.gunSayisi} gün
                        {i.kaynak && (
                          <> • <span className="text-purple-700 font-medium">{KAYNAK_ETIKETLERI[i.kaynak]}</span></>
                        )}
                        {i.sorumluAtamasi && <> • <span className="italic">Sorumlu atadı</span></>}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${durumRengi(i.durum)}`}>
                      <DurumIkonu durum={i.durum} />
                      {DURUM_ETIKETLERI[i.durum]}
                    </span>
                  </div>

                  {i.aciklama && (
                    <div className="bg-gray-50 rounded-lg p-2 mb-2 text-xs text-gray-700 flex items-start gap-1.5">
                      <MessageSquare className="w-3 h-3 flex-shrink-0 mt-0.5 text-gray-500" />
                      <span>{i.aciklama}</span>
                    </div>
                  )}

                  {i.durum === 'reddedildi' && i.redSebebi && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2 text-xs text-red-800">
                      <strong>Red sebebi:</strong> {i.redSebebi}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>
                      Talep: {i.talepTarihi.toDate().toLocaleDateString('tr-TR', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </span>
                    {iptalEdilebilir && (
                      <button
                        onClick={() => setIptalEdilecek(i)}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-semibold"
                      >
                        <Trash2 className="w-3 h-3" />
                        İptal Et
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        acik={!!iptalEdilecek}
        baslik="İzni İptal Et"
        mesaj={`${iptalEdilecek ? tarihEtiketi(iptalEdilecek.baslangicTarihi) : ''} tarihli izin talebini iptal etmek istediğine emin misin?`}
        onayMetni="İptal Et"
        tehlikeli
        onOnay={iptalEt}
        onIptal={() => setIptalEdilecek(null)}
      />
    </>
  )
}
